import { ApolloClient } from '@apollo/client';
import { includes } from 'lodash';

import {
  EntityGstStatus,
  EntityInEstateStatus,
  EntityKind,
  NonTrustEntityTaxStatus,
  TrustTaxStatus,
} from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';

import {
  entityGstStatusToApiTypeMap,
  estateInclusionStatusToApiTypeMap,
  nonTrustEntityTaxStatusToApiTypeMap,
  trustTaxStatusToApiTypeMap,
} from '../EntitySubforms/utils/shared/taxStatus.utils';
import {
  CltTrustTaxStatusFragment,
  CustodialNonTrustEntityTaxStatusFragment,
  DonorAdvisedFundEntityTaxStatusFragment,
  GratTrustTaxStatusFragment,
  IlitTrustTaxStatusFragment,
  IrrevocableTrustTaxStatusFragment,
  JointAccountTaxStatusFragment,
  PersonalAccountTaxStatusFragment,
  PrivateFoundationEntityTaxStatusFragment,
  QprtTrustTaxStatusFragment,
  QualifiedTuitionNonTrustEntityTaxStatusFragment,
  RevocableTrustTaxStatusFragment,
  SlatTrustTaxStatusFragment,
} from '../graphql/EntityTaxStatus.fragment.generated';
import { EntityType } from '../types/EntityType';
import { getEntityTypeFromEntityKind } from '../utils/getEntityTypeFromEntityKind';
import {
  GetTaxStatusDocument,
  GetTaxStatusQuery,
} from './graphql/TaxStatus.generated';
import {
  GRANTOR_TRUST,
  IN_ESTATE,
  NON_TAXABLE_ENTITY,
  NON_TAXABLE_TRUST,
  OUT_OF_ESTATE,
  TAXABLE_ENTITY,
} from './TaxStatusSubform.constants';
import {
  EstateInclusionStatus,
  GenericNonTrustEntityTaxStatus,
  GenericTrustTaxStatus,
  GSTStatus,
  TaxStatusSubformType,
  VariantType,
} from './TaxStatusSubform.types';

export const gstStatusToFormGSTStatus: Record<EntityGstStatus, GSTStatus> = {
  GST_EXEMPT: 'gst-exempt',
  GST_NON_EXEMPT: 'gst-non-exempt',
  MIXED_GST: 'mixed-gst',
};

export const inEstateStatusToFormEstateInclusionStatus: Record<
  EntityInEstateStatus,
  EstateInclusionStatus
> = {
  IN_ESTATE: 'in-estate',
  OUT_OF_ESTATE: 'out-of-estate',
};

const trustTaxStatusToFormTaxStatus: Record<
  TrustTaxStatus,
  GenericTrustTaxStatus
> = {
  GRANTOR_TRUST: 'grantor-trust',
  NON_GRANTOR_TRUST: 'non-grantor-trust',
  NON_TAXABLE_TRUST: 'non-taxable-trust',
};

const nonTrustEntityTaxStatusToFormTaxStatus: Record<
  NonTrustEntityTaxStatus,
  GenericNonTrustEntityTaxStatus
> = {
  TAXABLE: 'taxable-entity',
  NON_TAXABLE: 'non-taxable-entity',
};

function genericTrustToForm({
  trustTaxStatus,
  gstStatus,
  inEstateStatus,
  stateInEstateStatus,
  inclusionRatio,
}: {
  trustTaxStatus: TrustTaxStatus | null;
  gstStatus: EntityGstStatus | null;
  inEstateStatus: EntityInEstateStatus | null;
  stateInEstateStatus: EntityInEstateStatus | null;
  inclusionRatio?: TaxStatusSubformType['inclusionRatio'];
}) {
  return {
    nonTrustEntityTaxStatus: '' as const,
    taxStatus: trustTaxStatus
      ? trustTaxStatusToFormTaxStatus[trustTaxStatus]
      : ('' as const),
    gstStatus: gstStatus ? gstStatusToFormGSTStatus[gstStatus] : ('' as const),
    estateInclusionStatus: inEstateStatus
      ? inEstateStatusToFormEstateInclusionStatus[inEstateStatus]
      : 'in-estate',
    stateInEstateStatus: stateInEstateStatus
      ? inEstateStatusToFormEstateInclusionStatus[stateInEstateStatus]
      : null,
    inclusionRatio: inclusionRatio ?? null,
  };
}

function genericNonTrustEntityToForm({
  nonTrustEntityTaxStatus,
  gstStatus,
  inEstateStatus,
  stateInEstateStatus,
  inclusionRatio,
}: {
  nonTrustEntityTaxStatus: NonTrustEntityTaxStatus | null;
  gstStatus: EntityGstStatus | null;
  inEstateStatus: EntityInEstateStatus | null;
  stateInEstateStatus: EntityInEstateStatus | null;
  inclusionRatio?: TaxStatusSubformType['inclusionRatio'];
}) {
  return {
    taxStatus: '' as const,
    nonTrustEntityTaxStatus: nonTrustEntityTaxStatus
      ? nonTrustEntityTaxStatusToFormTaxStatus[nonTrustEntityTaxStatus]
      : ('' as const),
    gstStatus: gstStatus ? gstStatusToFormGSTStatus[gstStatus] : ('' as const),
    estateInclusionStatus: inEstateStatus
      ? inEstateStatusToFormEstateInclusionStatus[inEstateStatus]
      : 'in-estate',
    stateInEstateStatus: stateInEstateStatus
      ? inEstateStatusToFormEstateInclusionStatus[stateInEstateStatus]
      : null,
    inclusionRatio: inclusionRatio ?? null,
  };
}

type TaxStatusSubtypeFragment =
  | IrrevocableTrustTaxStatusFragment
  | RevocableTrustTaxStatusFragment
  | SlatTrustTaxStatusFragment
  | GratTrustTaxStatusFragment
  | IlitTrustTaxStatusFragment
  | QprtTrustTaxStatusFragment
  | CltTrustTaxStatusFragment
  | CustodialNonTrustEntityTaxStatusFragment
  | QualifiedTuitionNonTrustEntityTaxStatusFragment
  | DonorAdvisedFundEntityTaxStatusFragment
  | PrivateFoundationEntityTaxStatusFragment
  | PersonalAccountTaxStatusFragment
  | JointAccountTaxStatusFragment;

function getTaxStatusForEntity({
  entityType,
}: {
  entityType: TaxStatusSubtypeFragment;
}): TaxStatusSubformType {
  switch (entityType.__typename) {
    case 'IrrevocableTrust':
    case 'RevocableTrust':
    case 'SLATTrust':
    case 'ILITTrust':
    case 'QPRTTrust':
    case 'CLTTrust':
      return genericTrustToForm({
        trustTaxStatus: entityType.taxStatus ?? null,
        gstStatus: entityType.gstStatus ?? null,
        inEstateStatus: entityType.inEstateStatus ?? null,
        stateInEstateStatus: entityType.stateInEstateStatus ?? null,
        inclusionRatio: entityType.inclusionRatio ?? null,
      });

    case 'GRATTrust':
      // if all the GRAT's tax status fields are null we assume the GRAT is new and
      // we use default values, otherwise we use the values from the saved GRAT
      if (
        entityType.taxStatus === null &&
        entityType.gstStatus === null &&
        entityType.inEstateStatus === null
      ) {
        return genericTrustToForm({
          trustTaxStatus: TrustTaxStatus.GrantorTrust,
          gstStatus: null,
          inEstateStatus: EntityInEstateStatus.InEstate,
          stateInEstateStatus: EntityInEstateStatus.InEstate,
          inclusionRatio: entityType.inclusionRatio ?? null,
        });
      }

      return genericTrustToForm({
        trustTaxStatus: entityType.taxStatus ?? null,
        gstStatus: entityType.gstStatus ?? null,
        inEstateStatus: entityType.inEstateStatus ?? null,
        stateInEstateStatus: entityType.stateInEstateStatus ?? null,
        inclusionRatio: entityType.inclusionRatio ?? null,
      });

    case 'QualifiedTuitionPersonalAccount':
    case 'DonorAdvisedFund':
    case 'PrivateFoundation':
    case 'IndividualPersonalAccount':
    case 'JointPersonalAccount':
    case 'CustodialPersonalAccount':
      return genericNonTrustEntityToForm({
        nonTrustEntityTaxStatus: entityType.nonTrustEntityTaxStatus ?? null,
        gstStatus: entityType.gstStatus ?? null,
        inEstateStatus: entityType.nonTrustEntityInEstateStatus ?? null,
        stateInEstateStatus:
          ('nonTrustEntityStateInEstateStatus' in entityType
            ? entityType.nonTrustEntityStateInEstateStatus
            : null) ?? null,
        inclusionRatio: entityType.inclusionRatio ?? null,
      });

    default:
      throw new Error(`Unexpected entity type ${entityType.__typename}`);
  }
}

function dataToForm(data: GetTaxStatusQuery): TaxStatusSubformType {
  if (data.node?.__typename !== 'Entity') {
    throw new Error('Unexpected node type');
  }

  return getTaxStatusForEntity({
    entityType: data.node.subtype as TaxStatusSubtypeFragment,
  });
}

export async function taxStatusDataFetcher(
  apolloClient: ApolloClient<object>,
  nodeId: string
): Promise<TaxStatusSubformType> {
  const { data, error } = await apolloClient.query<GetTaxStatusQuery>({
    query: GetTaxStatusDocument,
    variables: {
      nodeId,
    },
  });

  if (error) {
    diagnostics.error('Could not fetch data for tax status subform', error);

    throw error;
  }

  return dataToForm(data);
}

export function getTaxStatusVariant(entityType: EntityType): VariantType {
  // there are other personal account entity types, but none that use the tax status subform
  if (
    includes(
      [
        'qualified-tuition-account',
        'custodial-account',
        'daf',
        'private-foundation',
      ],
      entityType
    )
  ) {
    return 'nonTrustEntity';
  }

  if (includes(['individual-account', 'joint-account'], entityType)) {
    return 'nonTrustEntityDefaultInEstate';
  }

  return 'default';
}

export const taxStatusDefaultValues: TaxStatusSubformType = {
  estateInclusionStatus: 'in-estate',
  stateInEstateStatus: null,
  taxStatus: '',
  nonTrustEntityTaxStatus: '',
  gstStatus: '',
  inclusionRatio: null,
};

export function getTaxStatusDefaultValues(
  variant: VariantType,
  entityType: EntityType
): TaxStatusSubformType {
  switch (variant) {
    case 'default': {
      const trustEntitySharedDefaults = {
        nonTrustEntityTaxStatus: '' as const, // nonTrustEntityTaxStatus is not used for the `default` variant
        stateInEstateStatus: null,
      };

      switch (entityType) {
        case 'irrevocable-trust':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: OUT_OF_ESTATE,
            gstStatus: '' as const,
            taxStatus: GRANTOR_TRUST,
            inclusionRatio: null,
          };
        case 'revocable-trust':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: IN_ESTATE,
            gstStatus: '' as const,
            taxStatus: GRANTOR_TRUST,
            inclusionRatio: null,
          };
        case 'slat':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: OUT_OF_ESTATE,
            gstStatus: '' as const,
            taxStatus: GRANTOR_TRUST,
            inclusionRatio: null,
          };
        case 'grat':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: IN_ESTATE,
            gstStatus: '' as const,
            taxStatus: GRANTOR_TRUST,
            inclusionRatio: null,
          };
        case 'ilit':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: OUT_OF_ESTATE,
            gstStatus: '' as const,
            taxStatus: '' as const,
            inclusionRatio: null,
          };
        case 'qprt':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: IN_ESTATE,
            gstStatus: '' as const,
            taxStatus: GRANTOR_TRUST,
            inclusionRatio: null,
          };
        case 'crt':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: OUT_OF_ESTATE,
            gstStatus: '' as const,
            taxStatus: NON_TAXABLE_TRUST,
            inclusionRatio: null,
          };
        case 'clt':
          return {
            ...trustEntitySharedDefaults,
            estateInclusionStatus: OUT_OF_ESTATE,
            gstStatus: '' as const,
            taxStatus: '' as const,
            inclusionRatio: null,
          };

        default:
          return taxStatusDefaultValues;
      }
    }
    case 'nonTrustEntity': {
      const nonTrustEntitySharedDefaults = {
        estateInclusionStatus: OUT_OF_ESTATE,
        stateInEstateStatus: null,
        taxStatus: '' as const, // taxStatus is not used for the `nonTrustEntity` variant
      };

      switch (entityType) {
        case 'custodial-account':
          return {
            ...nonTrustEntitySharedDefaults,
            gstStatus: '' as const,
            nonTrustEntityTaxStatus: TAXABLE_ENTITY,
            inclusionRatio: null,
          };

        case 'qualified-tuition-account':
          return {
            ...nonTrustEntitySharedDefaults,
            gstStatus: '' as const,
            nonTrustEntityTaxStatus: NON_TAXABLE_ENTITY,
            inclusionRatio: null,
          };
        case 'daf':
        case 'private-foundation':
          return {
            ...nonTrustEntitySharedDefaults,
            gstStatus: '' as const,
            nonTrustEntityTaxStatus: NON_TAXABLE_ENTITY,
            inclusionRatio: null,
          };

        default:
          throw new Error(`Unexpected entityType: ${entityType}`);
      }
    }
    case 'nonTrustEntityDefaultInEstate': {
      switch (entityType) {
        case 'individual-account':
        // Fall through to joint account
        case 'joint-account':
          return {
            taxStatus: '' as const,
            estateInclusionStatus: IN_ESTATE,
            stateInEstateStatus: null,
            gstStatus: '' as const, // gstStatus is not used for the `nonTrustEntityDefaultInEstate` variant
            nonTrustEntityTaxStatus: TAXABLE_ENTITY,
            inclusionRatio: null,
          };

        default:
          throw new Error(`Unexpected entityType: ${entityType}`);
      }
    }
  }
}

export function getTaxStatusValuesFromEntityKind(entityKind: EntityKind) {
  const entityType = getEntityTypeFromEntityKind(entityKind);
  return getTaxStatusValuesFromEntityType(entityType);
}

export function getTaxStatusValuesFromEntityType(entityType: EntityType) {
  const variant = getTaxStatusVariant(entityType);
  return getTaxStatusDefaultValues(variant, entityType);
}

export function getTaxStatusAPIValuesFromEntityType(entityType: EntityType) {
  const formValues = getTaxStatusValuesFromEntityType(entityType);

  return {
    taxStatus: formValues.taxStatus
      ? trustTaxStatusToApiTypeMap[formValues.taxStatus]
      : null,
    nonTrustEntityTaxStatus: formValues.nonTrustEntityTaxStatus
      ? nonTrustEntityTaxStatusToApiTypeMap[formValues.nonTrustEntityTaxStatus]
      : null,
    gstStatus: formValues.gstStatus
      ? entityGstStatusToApiTypeMap[formValues.gstStatus]
      : null,
    inEstateStatus:
      estateInclusionStatusToApiTypeMap[formValues.estateInclusionStatus],
  };
}
